import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const allMenuItems = [
  { path: '/dashboard', label: 'Dashboard', icon: '\u{1F4CA}', perm: 'dashboard' },
  { path: '/customers', label: 'Customers', icon: '\u{1F465}', perm: 'customers' },
  { path: '/loans', label: 'Loans', icon: '\u{1F4B5}', perm: 'loans' },
  { path: '/collections', label: 'Collections', icon: '\u{1F4CB}', perm: 'collections' },
  { path: '/my-balance', label: 'My Balance', icon: '\u{1F4B0}', perm: 'my_balance' },
  { path: '/accounts', label: 'Accounts', icon: '\u{1F3E6}', perm: 'accounts' },
  { path: '/expenses', label: 'Expenses', icon: '\u{1F4B8}', perm: 'expenses' },
  { path: '/income', label: 'Payments', icon: '\u{1F4C8}', perm: 'income' },
  { path: '/pl-sheet', label: 'P and L', icon: '\u{1F4C4}', adminOnly: true },
  { path: '/client-score', label: 'Client Score', icon: '\u{2B50}', adminOnly: true },
  { path: '/users', label: 'Users', icon: '\u{1F464}', adminOnly: true },
];

export default function Layout() {
  const { user, token, logout, hasPermission } = useAuth();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // All hooks must run unconditionally on every render (React's rules of
  // hooks) — this useEffect has to come before the early `if (!token)`
  // return below, not after it.
  useEffect(function() {
    function handleResize() {
      var mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) setSidebarOpen(true);
    }
    window.addEventListener('resize', handleResize);
    return function() { window.removeEventListener('resize', handleResize); };
  }, []);

  // Fixes AUDIT_REPORT M4: previously any of these pages could be reached
  // directly (e.g. a stale bookmark) with no token at all — they'd just
  // render broken/empty and spam failed API calls. `token` comes from
  // localStorage synchronously on first render, so this doesn't cause a
  // flash of the authenticated UI before redirecting.
  if (!token) {
    return React.createElement(Navigate, { to: '/login', replace: true });
  }

  var menuItems = allMenuItems.filter(function(item) {
    return item.adminOnly ? (user && user.role === 'admin') : hasPermission(item.perm);
  });

  function closeSidebar() {
    if (isMobile) setSidebarOpen(false);
  }

  return React.createElement('div', { style: { display: 'flex', height: '100vh', background: '#f3f4f6', overflow: 'hidden' } },
    // Mobile overlay
    isMobile && sidebarOpen && React.createElement('div', { onClick: function() { setSidebarOpen(false); }, style: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 40 } }),
    
    // Sidebar
    React.createElement('div', {
      style: {
        width: sidebarOpen ? (isMobile ? 260 : 240) : 0,
        minWidth: sidebarOpen ? (isMobile ? 260 : 240) : 0,
        background: 'linear-gradient(180deg, #1e293b 0%, #0f172a 100%)',
        color: 'white', display: 'flex', flexDirection: 'column',
        position: isMobile ? 'fixed' : 'relative', zIndex: 50,
        height: '100vh', transition: 'width 0.3s', overflow: 'hidden'
      }
    },
      React.createElement('div', { style: { padding: '14px 16px', borderBottom: '1px solid #374151', display: 'flex', justifyContent: 'space-between', alignItems: 'center' } },
        React.createElement('div', null,
          React.createElement('h1', { style: { fontSize: 18, fontWeight: 'bold', margin: 0 } }, '\u{1F4B0} Nirav Finance'),
          user && React.createElement('p', { style: { fontSize: 10, color: '#94a3b8', margin: '2px 0 0 0' } }, (user.full_name || user.username) + ' (' + user.role + ')')
        ),
        isMobile && React.createElement('button', { onClick: function() { setSidebarOpen(false); }, style: { background: 'none', border: 'none', color: 'white', fontSize: 22, cursor: 'pointer' } }, '\u2715')
      ),
      React.createElement('nav', { style: { flex: 1, padding: '8px 0', overflowY: 'auto' } },
        menuItems.map(function(item) {
          return React.createElement(NavLink, {
            key: item.path, to: item.path, onClick: closeSidebar,
            style: function(props) { return {
              display: 'flex', alignItems: 'center', padding: '12px 16px', margin: '2px 8px',
              borderRadius: 8, textDecoration: 'none', color: props.isActive ? 'white' : '#d1d5db',
              background: props.isActive ? '#2563eb' : 'transparent', transition: 'all 0.2s', fontSize: 15
            }; }
          },
            React.createElement('span', { style: { fontSize: 18 } }, item.icon),
            React.createElement('span', { style: { marginLeft: 10 } }, item.label)
          );
        })
      ),
      React.createElement('div', { style: { padding: '12px 16px', borderTop: '1px solid #374151' } },
        React.createElement('button', { onClick: function() { logout(); navigate('/login'); },
          style: { background: 'none', border: 'none', color: '#d1d5db', cursor: 'pointer', display: 'flex', alignItems: 'center', fontSize: 14, width: '100%', padding: '8px 0' } },
          React.createElement('span', { style: { fontSize: 18, marginRight: 8 } }, '\u{1F6AA}'), 'Logout'
        )
      )
    ),
    
    // Main Content
    React.createElement('div', { style: { flex: 1, overflow: 'auto', width: isMobile ? '100%' : 'auto' } },
      React.createElement('header', { style: { background: 'white', padding: '10px 16px', boxShadow: '0 1px 2px rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', position: 'relative', minHeight: 44 } },
        React.createElement('button', { onClick: function() { setSidebarOpen(true); }, style: { background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', padding: 0, display: isMobile ? 'block' : 'none', zIndex: 1 } }, '\u2630'),
        // Company name + logo, centered on every screen, as requested.
        React.createElement('div', { style: { position: 'absolute', left: 0, right: 0, textAlign: 'center', pointerEvents: 'none' } },
          React.createElement('h2', { style: { fontSize: 16, fontWeight: 600, color: '#1e293b', margin: 0 } }, '\u{1F4B0} Nirav Finance')
        )
      ),
      React.createElement('main', { style: { padding: isMobile ? 10 : 24, maxWidth: '100%', overflowX: 'hidden' } },
        React.createElement(Outlet)
      )
    )
  );
}
